/**
 * ---------------------------------------------------------------------------
 * MUTI SET TEMPLATE
 * ---------------------------------------------------------------------------
 * Steuert sections/main-product-set.liquid:
 *
 *  1. Info-Layer der Zeilen (Hover auf Zeigergeraeten kommt aus CSS, hier nur
 *     Tap/Klick, Escape und Klick nach aussen).
 *  2. Add to Cart mit ZWEI Positionen (Set + gewaehlter Gratis-Reiniger) in
 *     einem einzigen /cart/add.js Aufruf.
 *
 * Warum ein eigener ATC statt <form action="/cart/add">:
 *  - SlideCart HQ haengt sich per Delegate an '[action$="/cart/add"] [type=submit]'
 *    und wuerde nur die erste Position hinzufuegen.
 *  - Das Theme laeuft auf cart_type "page" und macht nach dem Submit einen harten
 *    Redirect auf /cart, bevor wir den Rabattcode setzen koennten.
 *
 * Rabattcode: /cart/update.js ersetzt die Codeliste vollstaendig. Bestehende
 * Codes werden deshalb erst gelesen und dann gemergt (gleiches Muster wie
 * snippets/auto-shipping-discount.liquid).
 */

class MutiSetForm extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-mset-atc]');
    this.stickyButton = this.querySelector('[data-mset-sticky-atc]');
    this.errorElement = this.querySelector('[data-mset-error]');
    this.giftInputs = Array.from(this.querySelectorAll('[data-mset-gift-input]'));
    this.giftNameTargets = Array.from(this.querySelectorAll('[data-mset-gift-name]'));

    this.variantId = this.getAttribute('variant');
    this.discountCode = (this.getAttribute('discount-code') || '').trim();
    this.giftRequired = this.hasAttribute('gift-required');
    this.verifyGift = this.hasAttribute('verify-gift');
    this.giftProperty = this.getAttribute('gift-property') || 'Gratis zum Set';
    this.giftPropertyValue = this.getAttribute('gift-property-value') || '';

    this.onGiftChange = this.onGiftChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.giftInputs.forEach((input) => input.addEventListener('change', this.onGiftChange));
    [this.button, this.stickyButton].forEach((button) => {
      if (button) button.addEventListener('click', this.onSubmit);
    });

    this.querySelectorAll('[data-mset-info-btn]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleRow(button.closest('[data-mset-row]'));
      });
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    this.syncGiftName();
    this.setupStickyBar();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);

    if (this.onViewportChange) {
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
    }
  }

  /* -------------------------------------------------------------------------
   * Info-Layer
   * ---------------------------------------------------------------------- */

  toggleRow(row) {
    if (!row) return;

    const isOpen = row.hasAttribute('data-mset-open');
    this.closeRows();

    if (!isOpen) {
      row.setAttribute('data-mset-open', '');
      const button = row.querySelector('[data-mset-info-btn]');
      if (button) button.setAttribute('aria-expanded', 'true');
    }
  }

  closeRows() {
    this.querySelectorAll('[data-mset-row][data-mset-open]').forEach((row) => {
      row.removeAttribute('data-mset-open');
      const button = row.querySelector('[data-mset-info-btn]');
      if (button) button.setAttribute('aria-expanded', 'false');
    });
  }

  onDocumentClick(event) {
    if (!event.target.closest('[data-mset-row]')) this.closeRows();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeRows();
  }

  /* -------------------------------------------------------------------------
   * Gratis-Auswahl
   * ---------------------------------------------------------------------- */

  get selectedGift() {
    return this.giftInputs.find((input) => input.checked && !input.disabled) || null;
  }

  onGiftChange() {
    this.hideError();
    this.syncGiftName();
  }

  syncGiftName() {
    const gift = this.selectedGift;
    const name = gift ? gift.getAttribute('data-mset-gift-title') : '';

    this.giftNameTargets.forEach((target) => {
      target.textContent = name;
      const wrapper = target.closest('[data-mset-gift-note]');
      if (wrapper) wrapper.hidden = name === '';
    });
  }

  /* -------------------------------------------------------------------------
   * Sticky Leiste
   * ---------------------------------------------------------------------- */

  /**
   * Bewusst kein IntersectionObserver: der feuert nur bei WECHSELN. Springt jemand direkt hinter den Kaufbereich
   * (Deep-Link, Zurueck-Navigation, Sprungmarke), bleibt der Zustand unveraendert und die Leiste taucht nie auf.
   * Ein gedrosselter Scroll-Listener liest stattdessen jedes Mal den echten Zustand.
   */
  setupStickyBar() {
    const bar = this.querySelector('[data-mset-sticky]');
    const anchor = this.querySelector('[data-mset-sticky-anchor]');

    if (!bar || !anchor) return;

    let ticking = false;

    const paint = () => {
      ticking = false;
      bar.toggleAttribute('data-mset-visible', anchor.getBoundingClientRect().bottom < 0);
    };

    this.onViewportChange = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    };

    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });

    paint();
  }

  /* -------------------------------------------------------------------------
   * Add to Cart
   * ---------------------------------------------------------------------- */

  get routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.isLoading) return;

    const gift = this.selectedGift;

    if (this.giftRequired && !gift) {
      this.showError(this.getAttribute('gift-error') || 'Bitte waehle deine Gratis-Reinigung.');
      const list = this.querySelector('[data-mset-gift-list]');
      if (list) list.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const items = [{ id: this.variantId, quantity: 1 }];

    if (gift) {
      const properties = {};
      properties[this.giftProperty] = this.giftPropertyValue || gift.getAttribute('data-mset-gift-title');

      items.push({ id: gift.value, quantity: 1, properties: properties });
    }

    this.setLoading(true);
    this.hideError();

    try {
      const cart = await this.addItems(items);

      if (this.discountCode) await this.applyDiscount(this.discountCode);

      let finalCart = await this.fetchCart();

      if (gift && this.verifyGift) {
        finalCart = await this.enforceFreeGift(finalCart, gift);
      }

      this.notifyCart(finalCart, cart);
    } catch (error) {
      this.showError(error && error.message ? error.message : 'Der Warenkorb konnte nicht aktualisiert werden.');
      console.error('[muti-set]', error);
    } finally {
      this.setLoading(false);
    }
  }

  async addItems(items) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    const body = { items: items };

    if (sections.length > 0) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(this.routeRoot + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });

    const json = await response.json();

    if (!response.ok) throw new Error(json.description || json.message || 'Add to cart failed');

    return json;
  }

  fetchCart() {
    return fetch(this.routeRoot + 'cart.js', { headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );
  }

  /**
   * /cart/update.js SETZT die Codeliste, es haengt nicht an. Vorhandene Codes
   * (z. B. MUTIVERSAND oder ein vom Kunden eingegebener Code) muessen deshalb
   * mitgeschickt werden, sonst fallen sie weg.
   */
  async applyDiscount(code) {
    const cart = await this.fetchCart();
    const existing = (cart.discount_codes || []).map((entry) => entry.code).filter(Boolean);

    if (existing.some((entry) => String(entry).toLowerCase() === code.toLowerCase())) return cart;

    existing.push(code);

    const response = await fetch(this.routeRoot + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: existing.join(',') })
    });

    return response.json();
  }

  /**
   * Fail-closed: ist der Gratis-Artikel nicht wirklich gratis (Rabatt im Admin
   * fehlt, abgelaufen oder nicht kombinierbar), fliegt er wieder raus statt dem
   * Kunden berechnet zu werden.
   */
  async enforceFreeGift(cart, gift) {
    const variantId = parseInt(gift.value, 10);
    const line = (cart.items || []).find(
      (item) => item.variant_id === variantId && item.properties && item.properties[this.giftProperty]
    );

    if (!line) return cart;

    const isFree = line.final_line_price === 0 || (cart.total_discount || 0) >= line.original_line_price;

    if (isFree) return cart;

    console.error(
      '[muti-set] Gratis-Artikel wurde nicht rabattiert und deshalb entfernt. Automatischen Rabatt oder Rabattcode im Shopify-Admin pruefen.'
    );

    const response = await fetch(this.routeRoot + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 })
    });

    this.showError(
      this.getAttribute('gift-unavailable-error') ||
        'Der Gratis-Artikel ist gerade nicht verfuegbar. Das Set liegt im Warenkorb, bitte melde dich kurz bei uns.'
    );

    return response.json();
  }

  /**
   * Der Theme-Drawer liest event.detail.cart.sections ungeprueft aus. Ohne
   * gebuendelte Sections deshalb cart:refresh feuern, das holt die Section selbst.
   */
  notifyCart(cart, addResponse) {
    if (addResponse && addResponse.sections) {
      cart.sections = addResponse.sections;
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } })
      );
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    if (typeof window.SLIDECART_UPDATE === 'function') window.SLIDECART_UPDATE();

    if (typeof window.SLIDECART_OPEN === 'function') {
      window.SLIDECART_OPEN();
      return;
    }

    // Ohne Drawer verhaelt sich die Seite wie der Rest des Stores (cart_type: page)
    window.location.href = this.routeRoot + 'cart';
  }

  setLoading(state) {
    this.isLoading = state;

    [this.button, this.stickyButton].forEach((button) => {
      if (!button) return;
      button.toggleAttribute('disabled', state);
      button.setAttribute('aria-busy', state ? 'true' : 'false');
    });

    document.documentElement.dispatchEvent(
      new CustomEvent(state ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;
    this.errorElement.hidden = true;
    this.errorElement.textContent = '';
  }
}

if (!window.customElements.get('muti-set-form')) {
  window.customElements.define('muti-set-form', MutiSetForm);
}
